/**
 * Hook for Patient Information field normalization and validation
 * Implements RA2 business rules: character normalization, auto-sync, calculated fields
 */

import { useCallback, useEffect } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { PatientFormSchemaType } from '@pages/main/patient/types';
import {
  normalizePostalCode,
  calculateAge,
  calculateGakunen,
} from '@utils/index';

interface UsePatientInfoNormalizationProps {
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  relationshipId: number | undefined;
}

export const usePatientInfoNormalization = ({
  watch,
  setValue,
  relationshipId,
}: UsePatientInfoNormalizationProps) => {
  // Watch fields that require normalization
  const postalCode1 = watch('patient_information.postal_code1');
  const postalCode2 = watch('patient_information.postal_code2');
  const address1 = watch('patient_information.address1');
  const dateOfBirth = watch('patient_information.date_of_birth');

  // Patient → insurance mirroring (postal/address/telephone/address_kana) is
  // handled in HealthInsuranceSection with a dirty-field guard so that
  // form.reset() on patient reload does not overwrite saved insurance values.
  // Do NOT re-sync those fields here — duplicate unguarded writes were the
  // cause of the 家族-address-overwrite regression.

  // Normalize postal code 1 (patient-side only)
  useEffect(() => {
    if (postalCode1) {
      const normalized = normalizePostalCode(postalCode1);
      if (normalized !== postalCode1) {
        setValue('patient_information.postal_code1', normalized);
      }
    }
  }, [postalCode1]);

  // Normalize postal code 2 (patient-side only)
  useEffect(() => {
    if (postalCode2) {
      const normalized = normalizePostalCode(postalCode2);
      if (normalized !== postalCode2) {
        setValue('patient_information.postal_code2', normalized);
      }
    }
  }, [postalCode2]);

  // RA2 business rule No.900: when 住所1 is cleared, also clear 住所カナ
  useEffect(() => {
    if (!address1) {
      setValue('patient_information.address_kana', '');
    }
  }, [address1, setValue]);

  // Calculate age from DOB and validate
  useEffect(() => {
    if (dateOfBirth) {
      // Age calculation is done on-demand by getAge() function
      // No need to store in component state
    }
  }, [dateOfBirth]);

  /**
   * Get age from DOB for validation purposes
   * Returns negative for future dates
   */
  const getAge = useCallback((): number => {
    if (!dateOfBirth) return 0;
    return calculateAge(dateOfBirth) ?? 0;
  }, [dateOfBirth]);

  /**
   * Get gakunen (school year) from DOB
   * Used for insurance type validation
   */
  const getGakunen = useCallback((): number => {
    if (!dateOfBirth) return 0;
    return calculateGakunen(dateOfBirth);
  }, [dateOfBirth]);

  /**
   * Sync patient name to insurance card unconditionally.
   * Always writes to insurance fields regardless of whether values are empty.
   */
  const syncPatientNameToInsuranceImmediate = useCallback(
    (patientName: string, patientNameKana: string) => {
      if (relationshipId === 2) {
        // 家族: surname only (first segment before space)
        const lastNameOnly = patientName.split(/[\s　]+/)[0] || '';
        const lastNameKanaOnly = patientNameKana.split(/[\s　]+/)[0] || '';
        setValue('health_insurance_card.name', lastNameOnly);
        setValue('health_insurance_card.name_kana', lastNameKanaOnly);
      } else {
        // 本人 (1) / 67（本人）(3) / 未選択: copy full name + kana
        setValue('health_insurance_card.name', patientName);
        setValue('health_insurance_card.name_kana', patientNameKana);
      }
    },
    [setValue, relationshipId]
  );

  /**
   * Sync patient name to insurance card
   * If relationship is 本人 (1), copy full name
   * If relationship is family (not 1), copy last name only
   *
   * When both 受診者氏名・カナ are empty, do not write to 被保険者氏名・カナ (e.g. 複写新規:
   * copyInit clears patient identity fields but keeps insurance holder name from source).
   */
  const syncPatientNameToInsurance = useCallback(
    (patientName: string, patientNameKana: string) => {
      const pn = (patientName ?? '').trim();
      const pk = (patientNameKana ?? '').trim();
      if (!pn && !pk) {
        return;
      }

      syncPatientNameToInsuranceImmediate(patientName, patientNameKana);
    },
    [syncPatientNameToInsuranceImmediate]
  );

  /**
   * Sync insurance name back to patient info
   * 本人 (1) / 67（本人）(3): full name + kana to patient
   * 家族 (2): replace surname segment only
   */
  const syncInsuranceNameToPatient = useCallback(
    (
      insuranceName: string,
      insuranceNameKana: string,
      patientName: string,
      patientNameKana: string
    ) => {
      if (relationshipId === 1 || relationshipId === 3) {
        // 本人 / 67（本人）: copy full name from insurance to patient
        if (insuranceName && insuranceName !== patientName) {
          setValue('patient_information.name', insuranceName);
        }
        if (insuranceNameKana && insuranceNameKana !== patientNameKana) {
          setValue('patient_information.name_kana', insuranceNameKana);
        }
      } else if (relationshipId === 2) {
        // 家族: update surname segment only
        if (insuranceName && insuranceName.trim() !== '') {
          const patientNameParts = patientName.split(/[\s　]+/);
          patientNameParts[0] = insuranceName;
          const updatedName = patientNameParts.join(' ');
          setValue('patient_information.name', updatedName);
        }
        if (insuranceNameKana && insuranceNameKana.trim() !== '') {
          const patientNameKanaParts = patientNameKana.split(/[\s　]+/);
          patientNameKanaParts[0] = insuranceNameKana;
          const updatedNameKana = patientNameKanaParts.join(' ');
          setValue('patient_information.name_kana', updatedNameKana);
        }
      }
    },
    [setValue, relationshipId]
  );

  return {
    getAge,
    getGakunen,
    syncPatientNameToInsurance,
    syncPatientNameToInsuranceImmediate,
    syncInsuranceNameToPatient,
  };
};
